import cx from 'classnames'
import 'intl-tel-input/styles'
import dynamic from 'next/dynamic'
import { useCallback, useEffect, useRef, useState } from 'react'

const IntlTelInput = dynamic(() => import('intl-tel-input/react'))

interface TelInputFieldProps {
  id: string
  name: string
  errorMessage?: string
  label?: string
  borderBottom?: boolean
  isInvertedColors?: boolean
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

const TelInputField = ({
  id,
  name,
  placeholder,
  className,
  label,
  errorMessage,
  borderBottom,
  isInvertedColors,
  onChange,
}: TelInputFieldProps) => {
  const [isMounted, setIsMounted] = useState(false)

  const lastValue = useRef<string>('')

  useEffect(() => {
    const mountTimeout = setTimeout(() => {
      setIsMounted(true)
    }, 100)

    return () => {
      clearTimeout(mountTimeout)
      setIsMounted(false)
    }
  }, [])

  const handleChange = useCallback(
    (value: string) => {
      if (lastValue.current === value) {
        return
      }

      lastValue.current = value

      onChange(value)
    },
    [onChange],
  )

  return (
    <div className={cx('grid', className)}>
      <div className={cx('flex flex-col relative text-left')}>
        {!!label && (
          <label htmlFor={id} className="text-xs mb-2">
            {label}
          </label>
        )}
        <div
          className={cx('flex justify-between', {
            'border-error': errorMessage,
            'border-input-border': !errorMessage && !isInvertedColors,
            'border-input-inverted-border': !errorMessage && isInvertedColors,
            'border-b': borderBottom,
            'border rounded-[3px]': !borderBottom,
          })}
        >
          {!isMounted && <div className="w-full h-[42px] bg-input-bg"></div>}
          {isMounted && (
            <IntlTelInput
              initialValue=""
              initOptions={{
                autoPlaceholder: 'off',
                initialCountry: 'dk',
                countryOrder: ['dk'],
                countrySearch: false,
                loadUtilsOnInit:
                  'https://cdn.jsdelivr.net/npm/intl-tel-input@24.8.2/build/js/utils.js',
                containerClass: 'intl-tel-input w-full',
              }}
              inputProps={{
                id,
                name,
                placeholder,
                className: cx(
                  'relative appearance-none w-full h-full py-3 px-4',
                  'leading-none',
                  'bg-input-bg text-input-text',
                ),
              }}
              onChangeNumber={handleChange}
            />
          )}
        </div>

        {!!errorMessage && (
          <span role="alert" className="mt-2 text-xs text-error">
            {errorMessage}
          </span>
        )}
      </div>
    </div>
  )
}

export default TelInputField
